import React, { useState, useEffect } from 'react';
import { Container, Spinner, Card, Modal, Alert, Button, Icon, Badge } from '../../../../core/ui';
import { useNavigate } from 'react-router-dom';
import SectionedTable from '../../../../core/common/SectionedTable';
import axios from '../../../../core/api/client';
import { useTranslation } from '../../../../core/context/TranslationContext';
import { formatShortDate, formatLongMonth, monthKey, dateSortValue } from '../../../../core/format/date';
import ReceiptDetailPanel from './ReceiptDetailPanel';
import useReceiptCardLinks from './useReceiptCardLinks';
import AccountantExportModal from '../export/AccountantExportModal';
import ScopeSwitcher from '../../../family/components/ScopeSwitcher';
import styles from './ReceiptHistory.module.css';

const ReceiptHistory = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [highlightedReceiptId, setHighlightedReceiptId] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const cardLinks = useReceiptCardLinks(receipts);
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await axios.get('/api/receipts');
        const sortedReceipts = response.data.sort((a, b) =>
          new Date(b.purchaseDateTime) - new Date(a.purchaseDateTime)
        );
        setReceipts(sortedReceipts);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReceipts();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const receiptId = params.get('receipt');
    if (receiptId) {
      setHighlightedReceiptId(parseInt(receiptId, 10));
    }
  }, []);

  const handleRowClick = (receipt) => {
    if (selectedReceipt && selectedReceipt.id === receipt.id) {
      setSelectedReceipt(null);
    } else {
      setSelectedReceipt(receipt);
    }
  };

  const handleEdit = (receipt) => {
    navigate(`/receipts/${receipt.id}/edit?receipt=${receipt.id}`);
  };

  const handleDelete = (receiptId) => {
    setReceiptToDelete(receiptId);
    setShowDeleteConfirmation(true);
    setSelectedReceipt(null);
  };

  const handleClosePanel = () => {
    setSelectedReceipt(null);
  };

  const confirmDelete = async () => {
    if (!receiptToDelete) return;

    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteSuccess(null);

    try {
      await axios.delete(`/api/receipts/${receiptToDelete}`);
      setDeleteSuccess(t('deleteSuccess'));

      const response = await axios.get('/api/receipts');
      const sortedReceipts = response.data.sort((a, b) =>
        new Date(b.purchaseDateTime) - new Date(a.purchaseDateTime)
      );
      setReceipts(sortedReceipts);
    } catch (err) {
      console.error('Delete error:', err);
      setDeleteError(err.response?.data?.message || t('deleteError'));
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirmation(false);
      setReceiptToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setReceiptToDelete(null);
  };

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '-';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const columns = [
    {
      key: 'date',
      header: t('date'),
      accessor: (r) => dateSortValue(r.purchaseDateTime),
      cell: (r) => formatShortDate(r.purchaseDateTime, language),
      filterValue: (r) => formatShortDate(r.purchaseDateTime, language),
    },
    {
      key: 'store',
      header: t('store'),
      accessor: (r) => r.storeName || '',
      cell: (r) => <span className={styles.storeCell}>{r.storeName || '—'}</span>,
    },
    {
      key: 'total',
      header: t('total'),
      align: 'end',
      accessor: (r) => Number(r.totalAmount || 0),
      cell: (r) => formatCurrency(r.totalAmount),
    },
    {
      key: 'items',
      header: t('items'),
      align: 'end',
      accessor: (r) => Number(r.itemCount || 0),
      cell: (r) => r.itemCount,
      filterable: false,
    },
    {
      key: 'card',
      header: t('receipts.payment'),
      accessor: (r) => (cardLinks[r.id] ? 1 : 0),
      cell: (r) => (cardLinks[r.id] ? (
        <Badge variant="accent" title={cardLinks[r.id].merchant}>
          <Icon name="fa-credit-card" size="sm" /> {t('receipts.card')}
        </Badge>
      ) : null),
      filterable: false,
    },
  ];

  const monthGroupBy = {
    key: (row) => monthKey(row.purchaseDateTime) || '0000-00',
    label: (row) => formatLongMonth(row.purchaseDateTime, language),
    order: (a, b) => b.localeCompare(a),
    trailing: (rows) => {
      const sum = rows.reduce((acc, r) => acc + (r.totalAmount || 0), 0);
      return `${rows.length} • ${formatCurrency(sum)}`;
    },
  };

  const rowClassName = (r) =>
    highlightedReceiptId !== null && r.id === highlightedReceiptId ? styles.highlightedRow : '';

  return (
    <Container>
      {loading ? (
        <div className={styles.loadingWrap}>
          <Spinner label={t('loading')} />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <Card>
            <Card.Header>
              <div className={styles.cardHeader}>
                <h5 className={styles.cardTitle}>{t('receiptHistory')}</h5>
                <div className={styles.headerActions}>
                  <ScopeSwitcher
                    current="mine"
                    mineRoute="/receipts"
                    familyRoute="/family/receipts"
                  />
                  <Button variant="secondary" size="sm" onClick={() => setShowExport(true)}>
                    <Icon name="fa-file-excel" size="sm" />
                    {t('accountantExport')}
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {deleteSuccess && (
                <Alert variant="success" dismissible onClose={() => setDeleteSuccess(null)}>
                  {deleteSuccess}
                </Alert>
              )}
              {deleteError && (
                <Alert variant="danger" dismissible onClose={() => setDeleteError(null)}>
                  {deleteError}
                </Alert>
              )}

              <SectionedTable
                data={receipts}
                columns={columns}
                rowKey={(r) => r.id}
                rowClassName={rowClassName}
                groupBy={monthGroupBy}
                initialSort={{ key: 'date', dir: 'desc' }}
                onRowClick={handleRowClick}
                emptyMessage={t('noData')}
                enableColumnFilters
              />
            </Card.Body>
          </Card>

          <Modal
            open={showDeleteConfirmation}
            onClose={cancelDelete}
            title={t('confirmDeletion')}
            footer={
              <>
                <Button variant="secondary" onClick={cancelDelete} disabled={deleteLoading}>
                  {t('cancel')}
                </Button>
                <Button variant="danger" onClick={confirmDelete} disabled={deleteLoading}>
                  {deleteLoading ? (
                    <>
                      <Spinner size="sm" /> {t('deleting')}...
                    </>
                  ) : (
                    <>
                      <Icon name="fa-trash" size={14} />
                      {t('delete')}
                    </>
                  )}
                </Button>
              </>
            }
          >
            <p>{t('confirmDeleteMessage')}</p>
            <p className={styles.mutedText}>{t('actionCannotBeUndone')}</p>
          </Modal>

          <AccountantExportModal open={showExport} onClose={() => setShowExport(false)} />

          <ReceiptDetailPanel
            receipt={selectedReceipt}
            cardLink={selectedReceipt ? cardLinks[selectedReceipt.id] : null}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onClose={handleClosePanel}
          />
        </>
      )}
    </Container>
  );
};

export default ReceiptHistory;
